import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { setSelectedClientId } from '@app/infrastructure/auth/setSelectedClientId';
import { logout } from '@app/service/auth/actions/logout';
import { loadClientList } from '@app/service/client/loaders/loadClientList';
import { useCallback } from 'react';

export function useLogin(
    authState: AuthState,
    handleSubmit: () => void,
    handleLoginOpen: (shouldOpen: boolean) => void,
    handleSelectClientOpen: (shouldOpen: boolean) => void,
    onAuthSuccess?: () => void,
) {
    const isAuthenticated = authState.authenticated;

    const handleLogout = useCallback(async () => {
        await logout();

        window.location.reload();
    }, []);

    const handleLoginClick = useCallback(() => {
        if (isAuthenticated && authState.clientSelected) {
            handleSubmit();
        } else {
            handleLoginOpen(true);
        }
    }, [isAuthenticated, authState.clientSelected, handleSubmit, handleLoginOpen]);

    const handleLoginClose = useCallback(() => {
        handleLoginOpen(false);
    }, [handleLoginOpen]);

    const handleSelectClientClose = useCallback(() => {
        handleSelectClientOpen(false);
    }, [handleSelectClientOpen]);

    const handleAuthSuccess = useCallback(() => {
        onAuthSuccess?.();
    }, [onAuthSuccess]);

    const handleLoginSuccess = useCallback(async () => {
        handleLoginClose();

        if (authState.clientSelected) {
            return handleAuthSuccess();
        }

        const clients = await loadClientList();

        if (clients.length === 1) {
            await setSelectedClientId(clients[0].id);
            handleAuthSuccess();
            handleSubmit();
            return;
        }

        if (clients.length === 0) {
            handleAuthSuccess();
            handleSubmit();
            return;
        }

        handleSelectClientOpen(true);
    }, [handleLoginClose, authState.clientSelected, handleSelectClientOpen, handleAuthSuccess, handleSubmit]);

    const handleSelectClientSuccess = useCallback(() => {
        handleSelectClientClose();

        handleAuthSuccess();
    }, [handleAuthSuccess, handleSelectClientClose]);

    return {
        handleLogout,
        handleLoginClick,
        handleLoginClose,
        handleLoginSuccess,
        handleSelectClientSuccess,
        handleSelectClientClose,
    };
}
